import React, { useEffect } from "react";
import PreviouslyServed from "./component/previous-tickets/PreviousUserTicket";
import WaitingList from "./component/ticket-card/WaitingList";
import { Card } from "@/components/ui/Card";
import { useDocumentTitle } from "usehooks-ts";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "@/app/store";
import { useGetStaffCounterDetailsQuery } from "../../../services/StaffsApi";
import { updateUserInfo } from "../../../features/auth/AuthSlice";
import { Button } from "../../ui/Button";
import { ErrorType } from "../../../app/types";
import { toast } from "@/components/ui/UseToast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { useGetCounterByIdQuery, useUpdateCounterOccupiedStatusMutation } from "../../../services/CounterApi";
import { useTicket } from "../../../context/TicketContext";
import AddTicketForm from "../tickets/component/modal/AddTicketFromAdmin";
import NowServingCard from "./component/serving-ticket/ServeTicket";
const ServeTicket = () => {
  const dispatch = useDispatch<AppDispatch>();
  useDocumentTitle("Serve Ticket");
  const { isCounterOpen : CounterOpen } = useTicket();
  const [addTicketFormOpen, setAddTicketFormOpen] = React.useState(false);

  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const services = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).service ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const AddTicket = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .hide_add_ticket ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const {
    data: counterData,
    refetch,
    error,
  } = useGetStaffCounterDetailsQuery({
    id: userInfo?.id ?? "",
  });



  const [updateCounterStatus, { isLoading:isCounterLoading }] =
    useUpdateCounterOccupiedStatusMutation();

  const [isCounterOpen, setIsCounterOpen] = React.useState(
    localStorage.getItem("counter_open") === "true"
  );

  const { data } = useGetCounterByIdQuery(userInfo?.counter_id || 0);

  useEffect(() => {
    if (data) {
      const isOccupied = data.is_occupied;
      const occupiedBy = data?.occupied_by;
      const currentUser = userInfo?.id;

      // Counter should be open only if occupiedBy matches current user
      const shouldOpen = isOccupied && occupiedBy === currentUser;

      setIsCounterOpen(shouldOpen);
      localStorage.setItem("counter_open", shouldOpen?.toString());
    }
  }, [data, userInfo?.id]);

  const [isLoading, setIsLoading] = React.useState(false);

  const handleOpenCounter = async () => {
    if (!userInfo?.counter_id || !userInfo?.id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Counter ID or User ID is missing.",
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await updateCounterStatus({
        id: userInfo.counter_id,
        staff_id: userInfo.id,
      }).unwrap().then((res) => {
        const newStatus = !isCounterOpen; // Assume the backend toggled it
        setIsCounterOpen(newStatus);

        localStorage.setItem("is_occupied", res.is_occupied.toString());
          toast({
            variant: "success",
            title: "Counter Opened",
            description: res.is_occupied ? "You can now start serving tickets." : "Counter Closed Successfully.",
          });
      });

    } catch (error: unknown) {
      console.error("Error", { error });
      const errorMessage =
        error && typeof error === "object" && "data" in error
          ? (error as ErrorType).data?.message ||
            (error as ErrorType).data?.detail
          : "Something Went Wrong.";
      toast({
        variant: "destructive",
        title: "Error in form Submission",
        description: errorMessage,
      });
    }finally{
      setIsLoading(false);
    }
  };

  // Handle refetch with error handling
  const handleRetry = async () => {
    try {
      const newData = await refetch();
      if (newData.error) {
        throw newData.error;
      }
      if (newData.data) {
        updateUserInfoState(newData.data);
      }
    } catch (error: unknown) {
      console.error("Error", { error });
      const errorMessage =
        error && typeof error === "object" && "data" in error
          ? (error as ErrorType).data?.message ||
            (error as ErrorType).data?.detail
          : "Something Went Wrong.";
      toast({
        variant: "destructive",
        title: "Error in form Submission",
        description: errorMessage,
      });
    }
  };
  // Run only on mount
 useEffect(() => {
  // Add debug log to see the values being compared
  // console.log("Checking data:", {
  //   counter: { userInfo: userInfo?.counter_id, counterData: counterData?.counter_id },
  //   service: { userInfo: userInfo?.service_id, counterData: counterData?.service_id },
  //   dept: { userInfo: userInfo?.department_id, counterData: counterData?.department_id }
  // });
  if (
    counterData && 
    (
      !userInfo?.counter_id || 
      userInfo?.counter_id !== counterData.counter_id ||
      userInfo?.service_id !== counterData.service_id ||
      userInfo?.department_id !== counterData.department_id
    )
  ) {
    
    updateUserInfoState(counterData);
  }
}, [counterData, userInfo?.counter_id, userInfo?.service_id, userInfo?.department_id]);


  // Function to update user info when refetch is called
  const updateUserInfoState = (counterData: {
    id: string;
    counter_id: number;
    service_name: string;
    service_id: number;
    department_id: number;
    department_name: string;
  }) => {
    if (counterData) {
      const updatedUserInfoData = {
        ...userInfo,
        counter_id: counterData.counter_id,
        service_name: counterData.service_name,
        service_id: counterData.service_id,
        department_id: counterData.department_id,
        department_name: counterData.department_name,
        token_type: userInfo?.token_type || "",
        id: userInfo?.id || "",
        first_name: userInfo?.first_name || "",
        last_name: userInfo?.last_name || "",
        username: userInfo?.username || "",
        email: userInfo?.email || "",
        org_id: Number(userInfo?.org_id) || 0,
        org_name: userInfo?.org_name || "",
      };
      dispatch(updateUserInfo(updatedUserInfoData));
      localStorage.setItem("userInfo", JSON.stringify(updatedUserInfoData));
    }
  };

  if (!userInfo?.counter_id || !userInfo?.service_id) {
    return (
      <div className="w-full h-full flex items-center justify-center pt-16 gap-4">
        <span className="text-primary font-semibold text-xl">
          You have not been assigned any counter to serve tickets.
        </span>
        <Button className="pl-2" onClick={handleRetry}>
          Retry
        </Button>
      </div>
    );
  }


  return (
    <div className="w-full p-5 bg-['#F8F9FD']">
      {services !== "read" ? (
        <>
          <div className="flex flex-row w-full items-center justify-between mb-5">
            <div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="pl-1 text-primary font-semibold text-xl capitalize text-nowrap max-w-[410ch] truncate">
                      Service: {userInfo?.service_name || ""}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{userInfo?.service_name}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            <div className="flex flex-row items-center gap-2">
              <Button
                           access={AddTicket === "write"}
                           className="gap-2"
                           onClick={() => setAddTicketFormOpen(true)} // Update to open the form
                         >
                           <i className="fa-solid fa-plus" />
                           Create New
                         </Button>
              <Button onClick={handleOpenCounter} disabled={isLoading}>
                {isLoading
                  ? "Opening..."
                  : isCounterOpen
                    ? "Close Counter"
                    : "Open Counter"}
              </Button>
            </div>
          </div>
          {CounterOpen ? (
            <p className="font-bold text-xl text-gray-500">Now Serving</p>
          ) : null}
          <div className="flex flex-col xlg:flex-row gap-5 w-full">
            <div className="flex flex-col lg:flex-row xlg:flex-col gap-3 xl:w-[65%]">
              <NowServingCard
                handleRetry={handleRetry}
                isCounterOpen={isCounterOpen}
              />
              <PreviouslyServed />
            </div>
            <div className="h-full w-full">
              <Card>
                <WaitingList />
              </Card>
            </div>
          </div>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <span className="text-primary font-semibold text-xl">
            You don't have Access to start serving tickets.
          </span>
        </div>
      )}
      <AddTicketForm
        initialDepartment={userInfo?.department_id}
            initialService={userInfo?.service_id}
            initialCounter={userInfo.counter_id}       
            isOpen={addTicketFormOpen}
              setOpen={setAddTicketFormOpen}
              refetch={refetch}
            />
    </div>
  );
};

export default ServeTicket;
